import { useEffect, useMemo, useState } from 'react'
import { Trans } from 'react-i18next'

import { BuilderPage, DefaultVariableResult } from '@makyoapp/makyo-common'
import { Tabs } from '@makyoapp/makyo-ui'

import { useAppPagesContext } from '@/context/AppPagesContext'
import { ComponentPanelTab } from '@/shared/builder-ui'
import { debounce } from '@/shared/utils'

import { PageSettingAction } from './PageSettingAction'
import { PageSettingBehaviour } from './PageSettingBehaviour'
import { PageSettingData } from './PageSettingData'
import { PageSettingDesign } from './PageSettingDesign'
import { SearchField } from './SearchField'

function PageSetting({ page, pageIdx }: { page: BuilderPage; pageIdx: number }) {
  const { updatePageAlt } = useAppPagesContext()

  const [searchData, setSearchData] = useState('')
  const [searchDesign, setSearchDesign] = useState('')
  const [searchAction, setSearchAction] = useState('')
  const [searchBehavior, setSearchBehavior] = useState('')

  let initialActiveKey: ComponentPanelTab.Data = ComponentPanelTab.Data

  const [activeKey, setActiveKey] = useState<ComponentPanelTab>(initialActiveKey)

  // const userProfilePage = pages.find((p) => p.type === PageType.UserProfile)

  // const isTblPage = !!page.tableId

  // const trailingElement = userProfilePage?.tbTrailingElements[0] as VariableResultMenu
  // const globalMenu = menus.find((m) => m.id === trailingElement?.value)

  // const [showIconPicker, setShowIconPicker] = useBoolean(false)
  // const [showFabIconPicker, setShowFabIconPicker] = useBoolean(false)
  // const [slugLoading, setSlugLoading] = useState<boolean>(false)

  // handle page title with variable text support
  // const [variablePageTitle, setVariablePageTitle] = useVariableTextEdit({
  //   value: page.title,
  //   onChange: (value, localValue) => {
  //     // validate & update slug here,
  //     // it will be done internally in the layer client
  //     const newSlug = generatePageSlug(pages, page.id, getTextFromSlateValue(localValue))
  //     const shouldUpdateSlug = newSlug !== page.slug

  //     updatePageAlt(pageIdx, (prev) => {
  //       prev.title = value
  //       prev.slug = shouldUpdateSlug ? newSlug : page.slug
  //     })

  //     if (shouldUpdateSlug) {
  //       goToPage({ slug: newSlug })
  //     }
  //   },
  // })

  // const [variablePageLabel, setVariablePageLabel] = useVariableTextEdit({
  //   value: page.label,
  //   onChange: (value) => {
  //     updatePageAlt(pageIdx, (prev) => {
  //       prev.label = value
  //     })
  //   },
  // })

  // const [variablePageSubTitle, setVariablePageSubTitle] = useVariableTextEdit({
  //   value: page.subTitle,
  //   onChange: (value) => {
  //     updatePageAlt(pageIdx, (prev) => {
  //       prev.subTitle = value
  //     })
  //   },
  // })

  // const [variablePageDescription, setVariablePageDescription] = useVariableTextEdit({
  //   value: page.description,
  //   onChange: (value) => {
  //     updatePageAlt(pageIdx, (prev) => {
  //       prev.description = value
  //     })
  //   },
  // })

  // const pageTable = useMemo(() => {
  //   return page.tableId ? tables.find((tb) => tb.id === page.tableId) : undefined
  // }, [page, tables])

  // const colOptions = useMemo(() => {
  //   return [
  //     { id: '', name: 'Select Column' } as ColumnAdapter,
  //     ...((pageTable &&
  //       pageTable.columns.filter((c) => {
  //         return DEFAULT_COLUMN_TYPES.includes(c.type)
  //       })) ||
  //       []),
  //   ]
  // }, [pageTable])

  // const leadingMenu = (page.tbLeadingElementLogOut as VariableResultMenu)?.value || ''
  // const trailingMenu = (page.tbTrailingElements?.[0] as VariableResultMenu)?.value || ''

  // const [localPageState, setLocalPageState] = useState<BuilderPage>({
  //   ...page,
  //   badgeMetadata: page.badgeMetadata as DefaultVariableResult, // Add default empty object if undefined
  // })

  const debouncedUpdatePageAlt = useMemo(
    () =>
      debounce(async <K extends keyof BuilderPage>(field: K, value: BuilderPage[K]) => {
        await updatePageAlt(pageIdx, (prev) => {
          prev[field] = value
        })
      }, 500),
    [pageIdx, updatePageAlt]
  )

  // Handle changes in the form fields
  // const handleFieldChange = useCallback(
  //   (field: keyof BuilderPage) => (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
  //     const value = e.currentTarget.value

  //     // Update local state immediately for UI responsiveness
  //     setLocalPageState((prev) => ({ ...prev, [field]: value }))

  //     // Trigger the debounced API update
  //     debouncedUpdatePageAlt(field, value)
  //   },
  //   [debouncedUpdatePageAlt]
  // )

  const debouncedUpdateBadgeMetadata = useMemo(
    () =>
      debounce(async (badgeMetadata: DefaultVariableResult) => {
        await updatePageAlt(pageIdx, (prev) => {
          prev.badgeMetadata = badgeMetadata
        })
      }, 800), // Adjust the debounce delay as needed
    [pageIdx, updatePageAlt]
  )

  // const handleBadgeMetadataChange = useCallback(
  //   async (value?: DefaultVariableResult | DefaultVariableResult[]) => {
  //     // Handle case when value is undefined
  //     if (!value) {
  //       // If value is undefined, clear the badge metadata
  //       setLocalPageState((prev) => ({ ...prev, badgeMetadata: undefined }))
  //       await updatePageAlt(pageIdx, (prev) => {
  //         prev.badgeMetadata = undefined
  //       })
  //       return
  //     }

  //     // If it's an array, extract the first element (assuming single selection is expected)
  //     const badgeMetadata = Array.isArray(value) ? value[0] : value

  //     setLocalPageState((prev) => ({
  //       ...prev,
  //       badgeMetadata,
  //     }))

  //     // Update both remote state and local state
  //     // debouncedUpdateBadgeMetadata(badgeMetadata)

  //     await updatePageAlt(pageIdx, (prev) => {
  //       prev.badgeMetadata = badgeMetadata
  //     })
  //   },
  //   [pageIdx, updatePageAlt]
  // )

  useEffect(() => {
    return () => {
      debouncedUpdatePageAlt.cancel()
      debouncedUpdateBadgeMetadata.cancel()
    }
  }, [debouncedUpdatePageAlt, debouncedUpdateBadgeMetadata])

  return (
    <Tabs activeKey={activeKey} defaultActiveKey={ComponentPanelTab.Data}>
      <Tabs.Nav
        onSelect={(eventKey) => {
          setActiveKey((eventKey as ComponentPanelTab) ?? ComponentPanelTab.Data)
        }}
      >
        <Tabs.NavItem eventKey={ComponentPanelTab.Data} className='mk-builder-component-nav-item'>
          <Trans>Data</Trans>
        </Tabs.NavItem>
        <Tabs.NavItem eventKey={ComponentPanelTab.Design} className='mk-builder-component-nav-item'>
          <Trans>Design</Trans>
        </Tabs.NavItem>
        <Tabs.NavItem eventKey={ComponentPanelTab.Action} className='mk-builder-component-nav-item'>
          <Trans>Action</Trans>
        </Tabs.NavItem>
        <Tabs.NavItem eventKey={ComponentPanelTab.Behavior} className='mk-builder-component-nav-item'>
          <Trans>Behavior</Trans>
        </Tabs.NavItem>
      </Tabs.Nav>

      <Tabs.Panel eventKey='data' mountOnEnter unmountOnExit noPadding>
        <SearchField search={searchData} setSearch={setSearchData} />
        <PageSettingData page={page} pageIdx={pageIdx} search={searchData} />
      </Tabs.Panel>

      <Tabs.Panel eventKey='design' mountOnEnter unmountOnExit noPadding>
        <SearchField search={searchDesign} setSearch={setSearchDesign} />
        <PageSettingDesign page={page} pageIdx={pageIdx} search={searchDesign} />
      </Tabs.Panel>

      <Tabs.Panel eventKey='action' mountOnEnter unmountOnExit noPadding>
        <SearchField search={searchAction} setSearch={setSearchAction} />
        <PageSettingAction page={page} pageIdx={pageIdx} search={searchAction} />
      </Tabs.Panel>

      <Tabs.Panel eventKey='behavior' mountOnEnter unmountOnExit noPadding>
        <SearchField search={searchBehavior} setSearch={setSearchBehavior} />
        <PageSettingBehaviour page={page} pageIdx={pageIdx} search={searchBehavior} />
      </Tabs.Panel>
    </Tabs>

    // TODO: just comment for now, will be removed later
    // <Accordion openMultiple={false} initialActiveItems={['page-settings']}>
    //   <AccordionItem itemId='page-settings' title={<Trans>Screen Settings</Trans>}>
    //     <Field>
    //       <label className='w-[60px]'>
    //         <Trans>Name</Trans>
    //       </label>
    //       {isTblPage ? (
    //         <SelectDropdown
    //           id='page-title-columns-source'
    //           withSearch={colOptions.length > SHOW_SEARCH_LIMIT}
    //           onChange={async (col) => {
    //             await updatePageAlt(pageIdx, (prev: { title: string }) => {
    //               prev.title = col.id
    //             })
    //           }}
    //           value={colOptions.find((col) => page.title === col.id)}
    //           optionLabel={(col) => `${col?.id} ${col?.name}`}
    //           options={colOptions.filter((col) => col.type !== ColumnTypes.Rating)}
    //           renderOption={(opt) => {
    //             return <RenderColumn withStyle={false} col={opt as unknown as ColumnAdapter} />
    //           }}
    //         />
    //       ) : (
    //         <div className='w-full pl-3'>
    //           <VariableTextV2
    //             value={variablePageTitle}
    //             onChange={(value) => {
    //               setVariablePageTitle(value)
    //             }}
    //             featureFlags={VariableTextFeatureFlag.SingleCell}
    //             variableColumnOptions={{
    //               columnTypes: [ColumnTypes.Text],
    //             }}
    //           />
    //         </div>
    //       )}
    //     </Field>

    //     <Field>
    //       <label className='w-[60px]'>
    //         <Trans>Slug</Trans>
    //       </label>
    //       <div className='flex w-full h-fit pl-3  relative'>
    //         {!!page.tableId ? (
    //           <SlugColumnDropdown
    //             tableId={page.tableId}
    //             value={page.slug || ''}
    //             onChange={(slugValue) => {
    //               debouncedUpdatePageAlt('slug', slugValue)
    //             }}
    //           />
    //         ) : (
    //           <>
    //             <SimpleInput
    //               id='page-slug'
    //               className='rtl:[direction:ltr] w-full'
    //               inputClassName='w-full pr-4'
    //               value={page.slug || ''}
    //               disabled
    //               // onChange={onSlugInputChange}
    //               // onBlur={() => handleUpdateSlug(slug)}
    //               // error={
    //               //   !isValidSlug ? <Trans>Invalid slug name</Trans> : slugError ? <Trans>{slugError}</Trans> : undefined
    //               // }
    //               readOnly
    //             />
    //             {slugLoading && <span className='w-4! h-4! loading spin absolute right-2 top-2.5' />}
    //           </>
    //         )}
    //       </div>
    //     </Field>
    //     {!isTblPage && (
    //       <Field>
    //         <label className='w-full'>
    //           <Trans>Icon</Trans>
    //         </label>
    //         <MaterialIconPicker
    //           searchMs={300}
    //           searchFilter={async (keyword) => {
    //             const aiSearch = await AIEmojisAPI.ReqFindEmoji(keyword)
    //             return aiSearch
    //           }}
    //           onClose={() => setShowIconPicker(false)}
    //           show={showIconPicker}
    //           clickAsDone
    //           onToggle={() => setShowIconPicker(true)}
    //           onDone={async (icon) => {
    //             await updatePageAlt(pageIdx, (prev: { icon: string }) => {
    //               prev.icon = icon
    //             })
    //             /*setPage({
    //              icon
    //              })*/
    //             setShowIconPicker(false)
    //           }}
    //           value={page.icon}
    //           className='h-fit rounded-md bg-zinc-200 dark:bg-slate-600'
    //           toggleClassName='p-1.5'
    //           portalMenu
    //           settings={{
    //             fill: 0,
    //             opsz: 24,
    //             wght: 300,
    //             grade: 0,
    //           }}
    //         />
    //       </Field>
    //     )}
    //   </AccordionItem>

    //   {!isTblPage && (
    //     <AccordionItem itemId='page-content' title={<Trans>Content</Trans>}>
    //       <Field>
    //         <label className='w-[115px]'>
    //           <Trans>Title</Trans>
    //         </label>
    //         {isTblPage && (
    //           <SelectDropdown
    //             id='page-title-columns-source'
    //             withSearch={colOptions.length > SHOW_SEARCH_LIMIT}
    //             onChange={async (col) => {
    //               await updatePageAlt(pageIdx, (prev) => {
    //                 prev.label = col.id
    //               })
    //             }}
    //             value={colOptions.find((col) => page.label === col.id)}
    //             optionLabel={(col) => `${col?.id} ${col?.name}`}
    //             options={colOptions}
    //             renderOption={(opt) => {
    //               return <RenderColumn withStyle={false} col={opt as unknown as ColumnAdapter} />
    //             }}
    //           />
    //         )}
    //         {!isTblPage && (
    //           <div className='w-full pl-3'>
    //             <VariableTextV2
    //               value={variablePageLabel}
    //               onChange={setVariablePageLabel}
    //               featureFlags={VariableTextFeatureFlag.SingleCell}
    //               variableColumnOptions={{
    //                 columnTypes: [ColumnTypes.Text],
    //               }}
    //             />
    //           </div>
    //         )}
    //       </Field>
    //       <Field>
    //         <label className='w-[115px]'>
    //           <Trans>Subtitle</Trans>
    //         </label>
    //         {isTblPage && (
    //           <SelectDropdown
    //             id='page-title-columns-source'
    //             withSearch={colOptions.length > SHOW_SEARCH_LIMIT}
    //             onChange={async (col) => {
    //               await updatePageAlt(pageIdx, (prev) => {
    //                 prev.subTitle = col.id
    //               })
    //             }}
    //             value={colOptions.find((col) => page.subTitle === col.id)}
    //             optionLabel={(col) => `${col?.id} ${col?.name}`}
    //             options={colOptions}
    //             renderOption={(opt) => {
    //               return <RenderColumn withStyle={false} col={opt as unknown as ColumnAdapter} />
    //             }}
    //           />
    //         )}
    //         {!isTblPage && (
    //           <div className='w-full pl-3'>
    //             <VariableTextV2
    //               value={variablePageSubTitle}
    //               onChange={setVariablePageSubTitle}
    //               featureFlags={VariableTextFeatureFlag.SingleCell}
    //               variableColumnOptions={{
    //                 columnTypes: [ColumnTypes.Text],
    //               }}
    //             />
    //           </div>
    //         )}
    //       </Field>
    //       <Field>
    //         <label className='w-[115px]'>
    //           <Trans>Description</Trans>
    //         </label>
    //         {isTblPage && (
    //           <SelectDropdown
    //             id='page-title-columns-source'
    //             withSearch={colOptions.length > SHOW_SEARCH_LIMIT}
    //             onChange={async (col) => {
    //               await updatePageAlt(pageIdx, (prev) => {
    //                 prev.description = col.id
    //               })
    //             }}
    //             value={colOptions.find((col) => page.description === col.id)}
    //             optionLabel={(col) => `${col?.id} ${col?.name}`}
    //             options={colOptions}
    //             renderOption={(opt) => {
    //               return <RenderColumn withStyle={false} col={opt as unknown as ColumnAdapter} />
    //             }}
    //           />
    //         )}
    //         {!isTblPage && (
    //           <div className='w-full pl-3'>
    //             <VariableTextV2
    //               value={variablePageDescription}
    //               onChange={setVariablePageDescription}
    //               featureFlags={VariableTextFeatureFlag.SingleCell}
    //               variableColumnOptions={{
    //                 columnTypes: [ColumnTypes.Text],
    //               }}
    //             />
    //           </div>
    //         )}
    //       </Field>
    //       <Field>
    //         <label className='w-[115px]'>
    //           <Trans>Cover Image</Trans>
    //         </label>
    //         <div className='w-full pl-3'>
    //           <VariableSelectFieldSetting
    //             id='coverImage'
    //             settingId='coverImage'
    //             variableSelectSettings={{
    //               flags: VariableSelectFeatureFlag.Image,
    //               columnTypes: [ColumnTypes.Image],
    //             }}
    //             value={page.coverImage}
    //             handleUpdate={async (value: string) => {
    //               await updatePageAlt(pageIdx, (prev) => {
    //                 prev.coverImage = value as any
    //               })
    //             }}
    //           />
    //         </div>
    //       </Field>
    //     </AccordionItem>
    //   )}

    // <AccordionItem itemId='bgColor' title={<Trans>Background</Trans>}>
    //   <Field isVertical>
    //     <label>Background Color</label>

    //     <BrandKitColorsDropdown
    //       type='bg'
    //       value={page.bgColor}
    //       onChange={async (value) => {
    //         await updatePageAlt(pageIdx, (prev: { bgColor: string }) => {
    //           prev.bgColor = value
    //         })
    //       }}
    //     />
    //   </Field>
    // </AccordionItem>

    // <AccordionItem itemId='alignment' title={<Trans>Alignment</Trans>}>
    //   <Field>
    //     <label>Content Alignment</label>

    //     <SingleIconSelect
    //       id='page-alignment'
    //       onClick={async (opt) => {
    //         await updatePageAlt(pageIdx, (prev) => {
    //           prev.alignment = opt.id as any
    //         })
    //       }}
    //       options={[
    //         {
    //           icon: <AlignLeft16 />,
    //           id: 'start',
    //           title: 'Start',
    //         },
    //         {
    //           icon: <AlignCenter16 />,
    //           id: 'center',
    //           title: 'Center',
    //         },
    //         {
    //           icon: <AlignRight16 />,
    //           id: 'end',
    //           title: 'End',
    //         },
    //       ]}
    //       selected={
    //         ['start', 'center', 'end'].includes(page.alignment as string) ? (page.alignment as string) : 'center'
    //       }
    //     />
    //   </Field>
    // </AccordionItem>

    //   {/* MARK: Top Bar */}
    //   {!isTblPage && (
    //     <AccordionItem itemId='top-bar-settings' title={<Trans>Top Navigation Bar</Trans>}>
    //       <div className='pb-4'>
    //         {!isTblPage && (
    //           <Field isVertical>
    //             <label>Type</label>
    //             <SelectDropdown
    //               value={page.tbType === TopBarType.AppBar ? 'App Bar' : 'Search Bar'}
    //               onChange={async (opt) => {
    //                 await updatePageAlt(pageIdx, (prev: { tbType: TopBarType }) => {
    //                   prev.tbType = opt === 'App Bar' ? TopBarType.AppBar : TopBarType.SearchBar
    //                 })
    //               }}
    //               renderOption={(opt) => {
    //                 return <Trans>{opt}</Trans>
    //               }}
    //               options={['App Bar', 'Search Bar']}
    //             />
    //           </Field>
    //         )}

    //         {page.tbType === TopBarType.AppBar && (
    //           <>
    //             <Field>
    //               <label className='flex-c4 w-full'>
    //                 <div className='w-full cursor-pointer'>
    //                   <Trans>Show Screen Title</Trans>
    //                 </div>
    //                 <div>
    //                   <Switch
    //                     checked={page.tbPageTitle}
    //                     onClick={async (tbPageTitle) => {
    //                       await updatePageAlt(pageIdx, (prev: { tbPageTitle: boolean }) => {
    //                         prev.tbPageTitle = tbPageTitle
    //                       })
    //                     }}
    //                   />
    //                 </div>
    //               </label>
    //             </Field>
    //             <Field>
    //               <label className='flex-c4 w-full'>
    //                 <div className='w-full cursor-pointer'>
    //                   <Trans>Use Global Settings</Trans>
    //                   <div className='flex-c text-light mt-1'>
    //                     <Menu16 />
    //                     <div>{parseLabel(globalMenu?.title || '')}</div>
    //                   </div>
    //                 </div>
    //                 <div>
    //                   <Switch
    //                     checked={page.tbUseGlobalSetting || false}
    //                     onClick={async (tbUseGlobalSetting) => {
    //                       await updatePageAlt(pageIdx, (prev) => {
    //                         prev.tbUseGlobalSetting = tbUseGlobalSetting
    //                       })
    //                     }}
    //                   />
    //                 </div>
    //               </label>
    //             </Field>
    //           </>
    //         )}

    //         {/* {!isTblPage && (
    //         <Field isVertical>
    //           <label>
    //             <Trans>Leading Menu</Trans>
    //           </label>

    //           <MenuSelectV2
    //             value={leadingMenu}
    //             onChange={async (menuId) => {
    //               await updatePageAlt(
    //                 pageIdx,
    //                 // @ts-ignore
    //                 (prev: { tbLeadingElementLogOut: DefaultVariableResult | undefined }) => {
    //                   prev.tbLeadingElementLogOut = {
    //                     flag: VariableSelectFeatureFlag.Menu,
    //                     id: '',
    //                     value: menuId,
    //                   } as unknown as VariableResultMenu
    //                 }
    //               )
    //             }}
    //           />
    //         </Field>
    //       )} */}
    //         {!page.tbUseGlobalSetting && (
    //           <Field isVertical>
    //             <label>
    //               <Trans>Menu</Trans>
    //             </label>

    //             <MenuSelectV2
    //               value={trailingMenu}
    //               onChange={async (menuId) => {
    //                 await updatePageAlt(pageIdx, (prev: { tbTrailingElements: DefaultVariableResult[] }) => {
    //                   prev.tbTrailingElements = [
    //                     {
    //                       flag: VariableSelectFeatureFlag.Menu,
    //                       id: '',
    //                       value: menuId,
    //                     } as unknown as VariableResultMenu,
    //                   ]
    //                 })
    //               }}
    //             />
    //           </Field>
    //         )}
    //       </div>
    //     </AccordionItem>
    //   )}

    //   {isTblPage && (
    //     <AccordionItem itemId='top-bar-settings' title={<Trans>Top Navigation Bar</Trans>}>
    //       <div className='pb-4'>
    //         {!isTblPage && (
    //           <Field isVertical>
    //             <label>Type</label>
    //             <SelectDropdown
    //               value={page.tbType === TopBarType.AppBar ? 'App Bar' : 'Search Bar'}
    //               onChange={async (opt) => {
    //                 await updatePageAlt(pageIdx, (prev: { tbType: TopBarType }) => {
    //                   prev.tbType = opt === 'App Bar' ? TopBarType.AppBar : TopBarType.SearchBar
    //                 })
    //               }}
    //               renderOption={(opt) => {
    //                 return <Trans>{opt}</Trans>
    //               }}
    //               options={['App Bar', 'Search Bar']}
    //             />
    //           </Field>
    //         )}

    //         {page.tbType === TopBarType.AppBar && (
    //           <>
    //             <Field>
    //               <label className='flex-c4 w-full'>
    //                 <div className='w-full cursor-pointer'>
    //                   <Trans>Show Screen Title</Trans>
    //                 </div>
    //                 <div>
    //                   <Switch
    //                     checked={page.tbPageTitle}
    //                     onClick={async (tbPageTitle) => {
    //                       await updatePageAlt(pageIdx, (prev: { tbPageTitle: boolean }) => {
    //                         prev.tbPageTitle = tbPageTitle
    //                       })
    //                     }}
    //                   />
    //                 </div>
    //               </label>
    //             </Field>
    //             <Field>
    //               <label className='flex-c4 w-full'>
    //                 <div className='w-full cursor-pointer'>
    //                   <Trans>Use Global Settings</Trans>
    //                   <div className='flex-c text-light mt-1'>
    //                     <Menu16 />
    //                     <div>{globalMenu?.title}</div>
    //                   </div>
    //                 </div>
    //                 <div>
    //                   <Switch
    //                     checked={page.tbUseGlobalSetting || false}
    //                     onClick={async (tbUseGlobalSetting) => {
    //                       await updatePageAlt(pageIdx, (prev) => {
    //                         prev.tbUseGlobalSetting = tbUseGlobalSetting
    //                       })
    //                     }}
    //                   />
    //                 </div>
    //               </label>
    //             </Field>
    //           </>
    //         )}
    //         {/*
    //            <Field>
    //            <label className='flex-c4 w-full'>
    //            <div className='w-full cursor-pointer'>
    //            <Trans>With Leading Menu</Trans>
    //            </div>
    //            <div>
    //            <Switch
    //            checked={page.tbLeadingMenu}
    //            onClick={(tbWithMenu) => {
    //            setPage({
    //            tbLeadingMenu: tbWithMenu
    //            })
    //            }}
    //            />
    //            </div>
    //            </label>
    //            */}
    //         {!isTblPage && (
    //           <Field isVertical>
    //             <label>
    //               <Trans>Leading Menu</Trans>
    //             </label>

    //             <MenuSelectV2
    //               value={leadingMenu}
    //               onChange={async (menuId) => {
    //                 await updatePageAlt(
    //                   pageIdx,
    //                   // @ts-ignore
    //                   (prev: { tbLeadingElementLogOut: DefaultVariableResult | undefined }) => {
    //                     prev.tbLeadingElementLogOut = {
    //                       flag: VariableSelectFeatureFlag.Menu,
    //                       id: '',
    //                       value: menuId,
    //                     } as unknown as VariableResultMenu
    //                   }
    //                 )
    //               }}
    //             />
    //           </Field>
    //         )}

    //         {!page.tbUseGlobalSetting && (
    //           <Field isVertical>
    //             <label>
    //               <Trans>Menu</Trans>
    //             </label>

    //             <MenuSelectV2
    //               value={trailingMenu}
    //               onChange={async (menuId) => {
    //                 await updatePageAlt(pageIdx, (prev: { tbTrailingElements: DefaultVariableResult[] }) => {
    //                   prev.tbTrailingElements = [
    //                     {
    //                       flag: VariableSelectFeatureFlag.Menu,
    //                       id: '',
    //                       value: menuId,
    //                     } as unknown as VariableResultMenu,
    //                   ]
    //                 })
    //               }}
    //             />
    //           </Field>
    //         )}
    //       </div>
    //     </AccordionItem>
    //   )}

    //   <AccordionItem itemId='navbar-settings' title={<Trans>Main Navigation Bar</Trans>}>
    //     <div className='pb-4'>
    //       <label className='w-full cursor-pointer flex-c'>
    //         <div>
    //           <Radio
    //             checked={page.bbType === BottomBarType.Global}
    //             value='on'
    //             name='bb-global-nav'
    //             onClick={async () => {
    //               await updatePageAlt(pageIdx, (prev: { bbType: BottomBarType }) => {
    //                 prev.bbType = BottomBarType.Global
    //               })
    //             }}
    //           />
    //         </div>
    //         <Trans>Show</Trans>
    //       </label>
    //     </div>

    //     {page.bbType === BottomBarType.Contextual && (
    //       <div className='ltr:pl-6 rtl:pr-6 pb-4'>
    //         <Field>
    //           <label className='w-[100px]'>Menu</label>
    //           <SelectDropdown
    //             value={menus.find((m) => m.id === page.bbContextualMenu)}
    //             onChange={async (m) => {
    //               // @ts-ignore
    //               await updatePageAlt(pageIdx, (prev: { bbContextualMenu: string }) => {
    //                 prev.bbContextualMenu = m.id
    //               })
    //             }}
    //             optionLabel={(m) => m?.title}
    //             renderOption={(m) => m?.title}
    //             options={menus}
    //           />
    //         </Field>
    //         <Field>
    //           <label htmlFor='display-fab' className='flex-c4 w-full'>
    //             <Trans>Display FAB</Trans>
    //           </label>
    //           <Switch
    //             id='display-fab'
    //             checked={page.bbContextualWithFab}
    //             onClick={async (bbContextualWithFab) => {
    //               // await updatePageAlt(pageIdx, (prev: { bbContextualWithFab: boolean }) => {
    //               //   prev.bbContextualWithFab = bbContextualWithFab
    //               // })
    //             }}
    //           />
    //         </Field>
    //         {page.bbContextualWithFab && (
    //           <Field>
    //             <label className='w-full'>
    //               <Trans>FAB Icon</Trans>
    //             </label>
    //             <MaterialIconPicker
    //               onClose={() => setShowFabIconPicker(false)}
    //               show={showFabIconPicker}
    //               clickAsDone
    //               onToggle={() => setShowFabIconPicker(true)}
    //               onDone={async (bbContextualFabIcon) => {
    //                 await updatePageAlt(pageIdx, (prev: { bbContextualFabIcon: string }) => {
    //                   prev.bbContextualFabIcon = bbContextualFabIcon
    //                 })
    //                 setShowFabIconPicker(false)
    //               }}
    //               searchMs={300}
    //               searchFilter={async (keyword) => {
    //                 const aiSearch = await AIEmojisAPI.ReqFindEmoji(keyword)
    //                 return aiSearch
    //               }}
    //               value={page.bbContextualFabIcon}
    //               className='rounded-md bg-zinc-200 dark:bg-slate-600'
    //               toggleClassName='p-2'
    //               placement='bottom-start'
    //               settings={{
    //                 fill: 0,
    //                 opsz: 24,
    //                 wght: 300,
    //                 grade: 0,
    //               }}
    //             />
    //           </Field>
    //         )}
    //         {page.bbContextualWithFab && (
    //           <Field>
    //             <label className='shrink-0 w-[100px] cursor-pointer'>FAB Actions</label>
    //             {/*
    //                  <ActionRepeater
    //                  id='bb-contextual-fab-actions'
    //                  values={page.bbContextualFabActions}
    //                  onChange={(bbContextualFabActions) => {

    //                  setPage({
    //                  bbContextualFabActions
    //                  })
    //                  }}
    //                  />
    //                  */}
    //             <div className='flex-c w-full min-w-[0px]'>
    //               <div className='min-w-[0px] w-full'>
    //                 {/* <ActionDropdown
    //                   value={page.bbContextualFabActions?.type}
    //                   onSelect={async (opt) => {
    //                     await updatePageAlt(pageIdx, (prev) => {
    //                       prev.bbContextualFabActions = {
    //                         ...(page.bbContextualFabActions || {
    //                           id: uniqueId('bb-fab-actions'),
    //                           settings: {},
    //                         }),
    //                         type: opt,
    //                       }
    //                     })
    //                   }}
    //                 /> */}
    //               </div>
    //               <Button
    //                 squared
    //                 noPadding
    //                 className='shrink-0'
    //                 defaultSize={false}
    //                 icon={<Edit16 />}
    //                 variant={ButtonVariant.Ghost}
    //               />
    //             </div>
    //           </Field>
    //         )}
    //       </div>
    //     )}

    //     <div className='pb-4'>
    //       <label className='w-full cursor-pointer flex-c'>
    //         <div>
    //           <Radio
    //             checked={page.bbType === BottomBarType.Disabled}
    //             value='on'
    //             name='bb-global-nav'
    //             onClick={async () => {
    //               await updatePageAlt(pageIdx, (prev: { bbType: BottomBarType }) => {
    //                 prev.bbType = BottomBarType.Disabled
    //               })
    //             }}
    //           />
    //         </div>
    //         <Trans>Hide</Trans>
    //       </label>
    //     </div>
    //   </AccordionItem>

    //   {!isTblPage && (
    //     <AccordionItem itemId='seo-settings' title={<Trans>SEO</Trans>}>
    //       <Field isVertical>
    //         <label>
    //           <Trans>Meta Title</Trans>
    //         </label>
    //         <SimpleInput value={localPageState.seoTitle} onChange={handleFieldChange('seoTitle')} />
    //       </Field>
    //       <Field isVertical>
    //         <label>
    //           <Trans>Meta Keywords</Trans>
    //         </label>
    //         <TextArea value={localPageState.seoKeywords} onChange={handleFieldChange('seoKeywords')} />
    //       </Field>
    //       <Field isVertical>
    //         <label>
    //           <Trans>Meta Description</Trans>
    //         </label>
    //         <TextArea value={localPageState.seoDescription} onChange={handleFieldChange('seoDescription')} />
    //       </Field>
    //     </AccordionItem>
    //   )}

    //   {isTblPage && (
    //     <AccordionItem itemId='seo-settings' title={<Trans>SEO</Trans>}>
    //       <Field>
    //         <label className='w-[120px]'>
    //           <Trans>Meta Title</Trans>
    //         </label>
    //         <SelectDropdown
    //           id='page-seo-meta-title-column'
    //           withSearch={colOptions.length > SHOW_SEARCH_LIMIT}
    //           onChange={async (col) => {
    //             await updatePageAlt(pageIdx, (prev) => {
    //               prev.seoTitle = col.id
    //             })
    //           }}
    //           value={colOptions.find((col) => page.seoTitle === col.id)}
    //           optionLabel={(col) => `${col?.id} ${col?.name}`}
    //           options={colOptions}
    //           renderOption={(opt) => {
    //             return <RenderColumn withStyle={false} col={opt as unknown as ColumnAdapter} />
    //           }}
    //         />
    //       </Field>
    //       <Field>
    //         <label className='w-[120px]'>
    //           <Trans>Meta Keywords</Trans>
    //         </label>
    //         <SelectDropdown
    //           id='page-seo-meta-keywords-column'
    //           withSearch={colOptions.length > SHOW_SEARCH_LIMIT}
    //           onChange={async (col) => {
    //             await updatePageAlt(pageIdx, (prev) => {
    //               prev.seoKeywords = col.id
    //             })
    //           }}
    //           value={colOptions.find((col) => page.seoKeywords === col.id)}
    //           optionLabel={(col) => `${col?.id} ${col?.name}`}
    //           options={colOptions}
    //           renderOption={(opt) => {
    //             return <RenderColumn withStyle={false} col={opt as unknown as ColumnAdapter} />
    //           }}
    //         />
    //       </Field>
    //       <Field>
    //         <label className='w-[120px]'>
    //           <Trans>Meta Description</Trans>
    //         </label>
    //         <SelectDropdown
    //           id='page-seo-meta-description-column'
    //           withSearch={colOptions.length > SHOW_SEARCH_LIMIT}
    //           onChange={async (col) => {
    //             await updatePageAlt(pageIdx, (prev) => {
    //               prev.seoDescription = col.id
    //             })
    //           }}
    //           value={colOptions.find((col) => page.seoDescription === col.id)}
    //           optionLabel={(col) => `${col?.id} ${col?.name}`}
    //           options={colOptions}
    //           renderOption={(opt) => {
    //             return <RenderColumn withStyle={false} col={opt as unknown as ColumnAdapter} />
    //           }}
    //         />
    //       </Field>
    //     </AccordionItem>
    //   )}

    //   {!isTblPage && (
    //     <AccordionItem itemId='badge-settings' title={<Trans>Badge Metadata</Trans>}>
    //       <Field isVertical>
    //         <VariableSelectV2
    //           id='badge-settings-metadata'
    //           placeholder='Select one element'
    //           value={
    //             isEmpty(localPageState.badgeMetadata)
    //               ? {
    //                   flag: VariableSelectFeatureFlag.CustomText,
    //                   id: uuid4(),
    //                   value: '',
    //                 }
    //               : localPageState.badgeMetadata
    //           }
    //           featureFlags={
    //             VariableSelectFeatureFlag.ColumnData |
    //             VariableSelectFeatureFlag.SingleCell |
    //             VariableSelectFeatureFlag.CustomText
    //           }
    //           variableColumnOptions={{
    //             columnTypes: BADGE_METADATA_COL_TYPES,
    //             excludeColIds: [],
    //           }}
    //           onChange={handleBadgeMetadataChange}
    //         />
    //       </Field>
    //     </AccordionItem>
    //   )}

    //   {!isTblPage && page && <BehaviorAccordion page={page} pageIdx={pageIdx} />}

    //   <ActionAccordion page={page} pageIdx={pageIdx} />

    //   {!isTblPage && <SearchAccordion page={page} pageIdx={pageIdx} />}
    // </Accordion>
  )
}

export default PageSetting
